import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Truck, Sun, Droplets, Wind, Battery, Zap, MapPin } from "lucide-react";
import QuickLeadForm from "@/components/QuickLeadForm";
import { ParallaxBg, ParallaxImage, FadeUp } from "@/components/Parallax";
import { IMG } from "@/lib/images";
import { TID } from "@/lib/testIds";

const FEATURES = [
  { icon: Zap, label: "Quiet Generator" },
  { icon: Sun, label: "Solar Powered" },
  { icon: Battery, label: "City Hookups" },
  { icon: MapPin, label: "Ready for Anywhere · Rural" },
];

export default function Home() {
  return (
    <div>
      {/* HERO — real trailer exterior at Zion with parallax */}
      <ParallaxBg
        image={IMG.heroExterior}
        minHeight="min-h-[100vh]"
        overlay="bg-gradient-to-b from-black/40 via-black/25 to-black/70"
        speed={0.25}
      >
        <div className="mx-auto max-w-7xl w-full h-full px-6 md:px-10 lg:px-16 pb-16 md:pb-24 flex items-end min-h-[100vh]">
          <FadeUp className="max-w-3xl text-ivory">
            <div className="overline text-ivory/70">Southern Utah · Luxury Portable Restrooms</div>
            <h1 className="h-display mt-4 text-5xl md:text-7xl leading-[1.02]">
              An Elevated<br/>Experience for<br/>Every Guest
            </h1>
            <div className="mt-5 text-champagne text-lg italic tracking-wide">Where comfort meets luxury.</div>
            <p className="mt-6 text-ivory/85 text-lg max-w-xl leading-relaxed">
              From intimate weddings to large community events, we provide clean, comfortable, and beautifully designed luxury restroom trailers — delivered anywhere in Southern Utah.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/booking" className="btn-primary" data-testid={TID.heroBookBtn}>
                Request Your Trailer <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/trailers" className="btn-secondary" data-testid={TID.heroViewTrailersBtn}>
                View Our Trailers
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-3 text-ivory/75 text-sm">
              <ShieldCheck className="h-4 w-4" /> Professionally delivered · climate-controlled · quiet generator
            </div>
          </FadeUp>
        </div>
      </ParallaxBg>

      {/* BRAND STATEMENT */}
      <section className="section relative grain">
        <div className="mx-auto max-w-5xl text-center">
          <FadeUp>
            <div className="overline">Luxury made simple</div>
            <h2 className="h-display mt-4 text-4xl md:text-6xl leading-tight">
              Comfort should never be an afterthought.
            </h2>
            <p className="mt-6 text-graphite text-lg leading-relaxed max-w-2xl mx-auto">
              Tell us about your event, select one or both of our three-stall luxury trailers, and let our team handle the rest.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="border-y border-stone bg-sand">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {FEATURES.map((f) => (
            <div key={f.label} className="flex items-center gap-3">
              <f.icon className="h-5 w-5 text-pebble" />
              <span className="text-sm md:text-[15px] text-charcoal font-medium tracking-wide">{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* OUR TRAILERS — real interior photos */}
      <section className="section bg-ivory">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <FadeUp>
              <div className="overline">Our Inventory</div>
              <h2 className="h-display mt-3 text-4xl md:text-5xl">Two matching three-stall luxury trailers.</h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="text-graphite max-w-md">
                Each trailer offers three private stalls, climate control, running water, and elegant finishes. Rent one or both.
              </p>
            </FadeUp>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-8">
            <FadeUp>
              <TrailerCard
                image={IMG.interiorSink}
                title="One Trailer"
                subtitle="Three private stalls"
                bestFor="Smaller and medium-sized gatherings"
                features={["1 luxury trailer", "3 private restroom stalls", "Delivery, setup & pickup"]}
                cta={{ to: "/booking?trailers=1", label: "Request One Trailer", tid: "request-one-trailer-btn" }}
              />
            </FadeUp>
            <FadeUp delay={0.15}>
              <TrailerCard
                image={IMG.interiorWide}
                title="Two Trailers"
                subtitle="Six private stalls"
                bestFor="Larger events, or separate restroom locations"
                features={["2 luxury trailers", "6 total restroom stalls", "Delivery, setup & pickup"]}
                cta={{ to: "/booking?trailers=2", label: "Request Both Trailers", tid: "request-two-trailers-btn" }}
                highlighted
              />
            </FadeUp>
          </div>

          <FadeUp>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Wind, label: "Climate control" },
                { icon: Droplets, label: "Running water" },
                { icon: Sun, label: "Interior lighting" },
                { icon: Truck, label: "Professional delivery" },
              ].map((f) => (
                <div key={f.label} className="flex items-center gap-3 p-5 rounded-2xl bg-white border border-stone">
                  <f.icon className="h-5 w-5 text-pebble" />
                  <span className="text-sm text-charcoal">{f.label}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PARALLAX BREAKAWAY — trailer at Zion */}
      <ParallaxBg
        image={IMG.heroExterior}
        minHeight="min-h-[90vh]"
        overlay="bg-gradient-to-r from-black/70 via-black/20 to-transparent"
        speed={0.32}
      >
        <div className="mx-auto max-w-7xl h-full px-6 md:px-10 lg:px-16 flex items-center min-h-[90vh]">
          <FadeUp className="max-w-xl text-ivory">
            <div className="overline text-ivory/70">On location</div>
            <h2 className="h-display mt-4 text-4xl md:text-6xl leading-[1.05]">
              From red-rock canyons<br/>to city streets.
            </h2>
            <p className="mt-6 text-ivory/80 text-lg leading-relaxed">
              Our trailers are ready for anywhere — Zion backdrops, private ranches, or downtown venues. Quiet generator, solar-ready, and city-hookup compatible.
            </p>
            <Link to="/service-area" className="mt-10 inline-flex items-center gap-2 rounded-full bg-champagne text-charcoal px-6 py-3 text-sm hover:bg-pebble transition-colors">
              Explore our service area <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </ParallaxBg>

      {/* INTERIOR CLOSE-UP SPLIT with parallax image */}
      <section className="section bg-ivory">
        <div className="mx-auto max-w-7xl grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <ParallaxImage src={IMG.interiorSink} alt="Trailer interior sink" className="h-[520px] rounded-3xl" speed={0.15} />
          <FadeUp>
            <div className="overline">Interior details</div>
            <h2 className="h-display mt-3 text-4xl md:text-5xl leading-[1.05]">
              Considered down to the last finish.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Bright, sunlit interiors with flattering lighting",
                "Elegant sinks, chrome fixtures, and generous mirrors",
                "Fresh water, hand soap, and thoughtful amenities",
                "SUER-branded touches your guests will notice",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="h-5 w-5 mt-0.5 text-pebble shrink-0" />
                  <span className="text-graphite leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      {/* EVENTS BENTO */}
      <section className="section bg-sand">
        <div className="mx-auto max-w-7xl">
          <FadeUp>
            <div className="max-w-2xl">
              <div className="overline">Events We Serve</div>
              <h2 className="h-display mt-3 text-4xl md:text-5xl">Elevated restrooms for every gathering.</h2>
            </div>
          </FadeUp>

          <div className="mt-12 grid grid-cols-6 auto-rows-[140px] md:auto-rows-[180px] gap-4">
            <BentoCard span="col-span-6 md:col-span-3 row-span-2" title="Weddings" img={IMG.wedding} />
            <BentoCard span="col-span-3 md:col-span-3 row-span-1" title="Concerts & Festivals" img={IMG.festival} />
            <BentoCard span="col-span-3 md:col-span-2" title="Corporate Events" img={IMG.champagne} />
            <BentoCard span="col-span-3 md:col-span-1" title="Private Parties" tone="champagne" />
            <BentoCard span="col-span-3 md:col-span-2" title="City Events" tone="sand" />
            <BentoCard span="col-span-3 md:col-span-2" title="Film Productions" tone="pebble" />
            <BentoCard span="col-span-3 md:col-span-2" title="Sporting Events" tone="champagne" />
            <BentoCard span="col-span-6 md:col-span-2" title="Multi-Day Events" tone="sand" />
          </div>

          <div className="mt-10">
            <Link to="/events" className="btn-ghost">See all event types <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-charcoal text-ivory">
        <div className="mx-auto max-w-7xl">
          <div className="grid md:grid-cols-5 gap-10">
            <FadeUp className="md:col-span-2">
              <div className="overline text-ivory/60">How It Works</div>
              <h2 className="h-display mt-3 text-4xl md:text-5xl">Four elegant steps.</h2>
              <p className="mt-6 text-ivory/70 leading-relaxed max-w-md">
                Start with a simple online request and our team replies within 24 hours to confirm availability and coordinate a beautifully prepared trailer, on time, at your venue.
              </p>
              <Link to="/booking" className="mt-8 inline-flex items-center gap-2 rounded-full bg-champagne text-charcoal px-6 py-3 text-sm hover:bg-pebble transition-colors">
                Start your request <ArrowRight className="h-4 w-4" />
              </Link>
            </FadeUp>
            <div className="md:col-span-3 grid sm:grid-cols-2 gap-4">
              {[
                { n: "01", t: "Tell Us About Your Event", d: "Date, location, guest count, event type." },
                { n: "02", t: "Choose One or Two Trailers", d: "Select the number of three-stall luxury trailers." },
                { n: "03", t: "Send Your Request", d: "Submit your event details in about five minutes." },
                { n: "04", t: "We Confirm & Deliver", d: "Our team replies within 24 hours to confirm and coordinate delivery." },
              ].map((s, i) => (
                <FadeUp key={s.n} delay={i * 0.08}>
                  <div className="rounded-3xl border border-white/10 p-6 bg-white/5 backdrop-blur-sm h-full">
                    <div className="h-display text-3xl text-champagne">{s.n}</div>
                    <div className="mt-3 font-medium">{s.t}</div>
                    <p className="mt-2 text-ivory/70 text-sm">{s.d}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="section">
        <div className="mx-auto max-w-7xl grid md:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <div className="overline">Service Area</div>
            <h2 className="h-display mt-3 text-4xl md:text-5xl">Throughout the red-rock country.</h2>
            <p className="mt-5 text-graphite leading-relaxed">
              We serve St. George, Washington, Hurricane, Ivins, Santa Clara, Cedar City, Springdale, Zion area, Kanab, Enterprise, Brian Head, Beaver, and surrounding communities.
            </p>
            <Link to="/service-area" className="btn-ghost mt-8">View coverage map <ArrowRight className="h-4 w-4" /></Link>
          </FadeUp>
          <ParallaxImage src={IMG.zion} alt="Zion National Park" className="h-[420px] rounded-3xl" speed={0.18} />
        </div>
      </section>

      {/* QUICK LEAD FORM */}
      <section className="section bg-sand" id="quick-estimate">
        <div className="mx-auto max-w-3xl">
          <FadeUp>
            <QuickLeadForm />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

function TrailerCard({ image, title, subtitle, bestFor, features, cta, highlighted }) {
  return (
    <div className={`card-soft overflow-hidden ${highlighted ? "ring-1 ring-pebble" : ""}`}>
      <div className="relative h-72 overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
      </div>
      <div className="p-8">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="h-display text-3xl">{title}</h3>
          <span className="overline">{subtitle}</span>
        </div>
        <div className="mt-3 text-sm text-graphite italic">{bestFor}</div>
        <ul className="mt-5 space-y-2">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 mt-0.5 text-pebble shrink-0" /> <span>{f}</span>
            </li>
          ))}
        </ul>
        <Link to={cta.to} data-testid={cta.tid} className="btn-primary mt-8 w-full">{cta.label} <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}

function BentoCard({ span, title, img, tone }) {
  const bg = tone === "champagne" ? "bg-champagne" : tone === "sand" ? "bg-white" : tone === "pebble" ? "bg-pebble" : "";
  return (
    <div className={`${span} relative rounded-3xl overflow-hidden border border-stone group ${bg}`}>
      {img && <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      {img && <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />}
      <div className={`relative h-full flex items-end p-5 md:p-6 ${img ? "text-ivory" : "text-charcoal"}`}>
        <div className="h-display text-xl md:text-2xl leading-tight">{title}</div>
      </div>
    </div>
  );
}
